import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import { HttpError, LoadCurveSummaryResponse } from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import {
  getLoadCurveSummary,
  GetLoadCurveSummary$Params,
} from '../../../core/clients/omni-core/functions';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { OrganizationsStore } from '../../organizations/store/organizations.store';
import { de } from 'date-fns/locale';
import { AnalysisGranularity } from '../chart-config/util';

export interface LoadCurveState {
  id: string;
  data: LoadCurveSummaryResponse | null;

  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface ConsumptionFilter {
  view: 'line' | 'bar' | 'table';
  granularity: AnalysisGranularity;
  date: Date;
  data: {
    utilityType: 'ELECTRICITY' | 'GAS';
    direction: 'CONSUMPTION' | 'PRODUCTION';
    locationID: string;
    energyInterfaceID: string;
  };
}

const getLoadCurveSummaryGranularity = (
  filter: ConsumptionFilter,
): GetLoadCurveSummary$Params['granularity'] => {
  switch (filter.granularity) {
    case 'YEAR':
      return filter.view === 'bar' ? 'MONTH' : 'DAY';
    case 'MONTH':
      return 'DAY';
    case 'WEEK':
      return filter.view === 'bar' ? 'DAY' : 'HOUR';
    case 'DAY':
      return filter.view === 'bar' ? 'HOUR' : 'QUARTER_HOUR';
  }
};

export const getInitialFilter = (): ConsumptionFilter => ({
  view: 'line',
  granularity: 'YEAR',
  date: new Date(),
  data: {
    utilityType: 'ELECTRICITY',
    direction: 'CONSUMPTION',
    locationID: '',
    energyInterfaceID: '',
  },
});

export const isFilterEqual = (a: ConsumptionFilter, b: ConsumptionFilter): boolean =>
  filterToId(a) === filterToId(b);

export const filterToId = (filter: ConsumptionFilter): string => {
  const postId = `${filter.view}-${filter.data.utilityType}-${filter.data.direction}-${filter.data.locationID}-${filter.data.energyInterfaceID}`;
  switch (filter.granularity) {
    case 'YEAR':
      return `YEAR-${filter.date.getFullYear()}-${postId}`;
    case 'MONTH':
      return `MONTH-${format(filter.date, 'yyyy-MM')}-${postId}`;
    case 'WEEK':
      return `WEEK-${format(filter.date, 'yyyy-ww')}-${postId}`;
    case 'DAY':
      return `DAY-${format(filter.date, 'yyyy-MM-dd')}-${postId}`;
  }
};

export const getSelectedFrom = (filter: ConsumptionFilter): Date => {
  switch (filter.granularity) {
    case 'YEAR':
      return startOfYear(filter.date);
    case 'MONTH':
      return startOfMonth(filter.date);
    case 'WEEK':
      return startOfWeek(filter.date, { locale: de });
    default:
      return filter.date;
  }
};

export const getSelectedTo = (filter: ConsumptionFilter): Date => {
  switch (filter.granularity) {
    case 'YEAR':
      return endOfYear(filter.date);
    case 'MONTH':
      return endOfMonth(filter.date);
    case 'WEEK':
      return endOfWeek(filter.date, { locale: de });
    default:
      return filter.date;
  }
};

@Injectable()
export class ConsumptionStore {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);

  filter = signal<ConsumptionFilter>(getInitialFilter());

  #data = signal<Record<string, LoadCurveState>>({});
  data = computed(this.#data);

  private patchItem(id: string, newData: Partial<LoadCurveState>) {
    this.#data.update((data) => ({ ...data, [id]: { ...data[id], ...newData } }));
  }

  get(filter: ConsumptionFilter): LoadCurveState | null {
    const id = filterToId(filter);
    return this.#data()[id] ?? null;
  }

  async load(filter: ConsumptionFilter): Promise<void> {
    const id = filterToId(filter);
    this.#data.update((data) => ({
      ...data,
      [id]: {
        id,
        data: null,
        isLoading: true,
        error: null,
      },
    }));
    try {
      const oid = untracked(() => this.organizationsStore.selectedId());
      if (!oid) {
        throw new Error('No organization selected');
      }
      const res = await this.api.invoke(getLoadCurveSummary, {
        oid,
        granularity: getLoadCurveSummaryGranularity(filter),
        direction: filter.data.direction,
        locationID: filter.data.energyInterfaceID ? undefined : filter.data.locationID || undefined,
        energyInterfaceID: filter.data.energyInterfaceID || undefined,
        utilityType: filter.data.utilityType,
        from: format(getSelectedFrom(filter), 'yyyy-MM-dd'),
        to: format(getSelectedTo(filter), 'yyyy-MM-dd'),
      });
      this.patchItem(id, { data: res });
    } catch (error) {
      this.patchItem(id, { error });
    } finally {
      this.patchItem(id, { isLoading: false });
    }
  }

  onChangeFilter() {
    const filter = this.filter();
    if (this.get(filter)) {
      return;
    }
    return this.load(this.filter());
  }

  reset() {
    this.#data.set({});
  }
}
